import React, { useEffect, useState, useRef } from "react";
import { Palette, Check } from "lucide-react";

const THEMES = [
  {
    id: "theme-1",
    name: "Option A — BharatKosh Classic",
    desc: "Navy Blue (#0F2942) & Saffron Amber (#D97706)",
    swatches: ["#0F2942", "#D97706", "#F8FAFC"]
  },
  {
    id: "theme-2",
    name: "Option B — Financial Blue & Teal",
    desc: "Navy (#153653), Teal (#168477) & Pop Orange (#EA580C)",
    swatches: ["#153653", "#168477", "#EA580C", "#E5F3F0"]
  },
  {
    id: "theme-3",
    name: "Option C — Sovereign MSME Green",
    desc: "Forest (#0C3A30), Olive (#6E9419) & Warm White (#F7F9F4)",
    swatches: ["#0C3A30", "#6E9419", "#E8F0E6", "#F7F9F4"]
  }
];

export function ThemePaletteToggle({ dark }) {
  const [open, setOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState("theme-2");
  const popoverRef = useRef(null);

  useEffect(() => {
    const saved = localStorage.getItem("bharatkosh_theme") || "theme-2";
    setCurrentTheme(saved);
    document.documentElement.setAttribute("data-theme", saved);
  }, []);

  const changeTheme = (themeId) => {
    setCurrentTheme(themeId);
    document.documentElement.setAttribute("data-theme", themeId);
    localStorage.setItem("bharatkosh_theme", themeId);
    setOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="relative inline-block" ref={popoverRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        title="Change Color Theme Palette"
        className={`w-9 h-9 rounded-full flex items-center justify-center border transition-all ${
          dark
            ? "border-white/50 text-white bg-white/10 hover:bg-white/20"
            : "border-slate-200 text-slate-700 bg-white hover:border-slate-400 shadow-sm"
        }`}
      >
        <Palette className="w-4 h-4" />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-slate-200 p-3 z-50 text-slate-800 animate-in fade-in zoom-in-95 duration-150">
          <div className="text-xs font-bold text-slate-700 px-2 py-1 mb-2 border-b border-slate-100 flex items-center gap-1.5">
            <Palette className="w-3.5 h-3.5 text-slate-500" />
            <span>Select Design Theme</span>
          </div>

          <div className="space-y-1.5">
            {THEMES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => changeTheme(t.id)}
                className={`w-full text-left p-2 rounded-lg border transition-all flex items-center justify-between ${
                  currentTheme === t.id
                    ? "border-slate-900 bg-slate-50 shadow-xs"
                    : "border-slate-100 hover:border-slate-300 hover:bg-slate-50/50"
                }`}
              >
                <div>
                  <div className="text-xs font-semibold text-slate-900 flex items-center gap-1">
                    {t.name}
                    {currentTheme === t.id && <Check className="w-3 h-3 text-emerald-600 inline" />}
                  </div>
                  <div className="text-[10px] text-slate-500 line-clamp-1">{t.desc}</div>
                </div>
                <div className="flex gap-0.5 ml-2 shrink-0">
                  {t.swatches.map((color, idx) => (
                    <span
                      key={idx}
                      className="w-3.5 h-3.5 rounded-full border border-black/10 inline-block"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

